import React from 'react';
import { TableCell, Box, Tooltip } from '@mui/material';
import { ArrowUpward, ArrowDownward, UnfoldMore } from '@mui/icons-material';

/**
 * SortableTableCell - Column header with integrated sorting functionality
 * Displays sorting icons inline with the column label for better space efficiency
 *
 * @param {string} label - Column label text
 * @param {string} sortBy - Sort key for this column
 * @param {string} currentSortBy - Currently active sort key
 * @param {string} currentSortOrder - Current sort order ('asc' or 'desc')
 * @param {function} onSort - Callback when clicking to sort
 * @param {string} align - Text alignment ('left', 'right', 'center')
 * @param {string} tooltip - Optional tooltip text
 * @param {object} sx - Additional Material-UI sx props
 */
const SortableTableCell = ({
  label,
  sortBy,
  currentSortBy,
  currentSortOrder,
  onSort,
  align = 'left',
  tooltip,
  sx = {},
}) => {
  const isActive = currentSortBy === sortBy;
  const isAscending = isActive && currentSortOrder === 'asc';
  const isDescending = isActive && currentSortOrder === 'desc';

  const handleClick = () => {
    if (onSort) {
      onSort(sortBy);
    }
  };

  const getSortIcon = () => {
    if (isAscending) {
      return <ArrowUpward sx={{ fontSize: '1rem', opacity: 0.9 }} />;
    }
    if (isDescending) {
      return <ArrowDownward sx={{ fontSize: '1rem', opacity: 0.9 }} />;
    }
    return <UnfoldMore sx={{ fontSize: '1rem', opacity: 0.3 }} />;
  };

  const cellContent = (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        justifyContent:
          align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        width: '100%',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          color: 'primary.main',
          '& .sort-icon': {
            opacity: isActive ? 0.9 : 0.6,
            transform: 'scale(1.1)',
          },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: isActive ? 700 : 600,
          color: isActive ? 'primary.main' : 'text.primary',
          transition: 'color 0.2s ease-in-out',
        }}
      >
        {label}
      </Box>
      <Box
        className="sort-icon"
        sx={{
          display: 'flex',
          alignItems: 'center',
          transition: 'all 0.2s ease-in-out',
          color: isActive ? 'primary.main' : 'text.secondary',
        }}
      >
        {getSortIcon()}
      </Box>
    </Box>
  );

  return (
    <TableCell
      align={align}
      sx={{
        fontWeight: 'bold',
        backgroundColor: '#f8fafc',
        padding: '8px 12px',
        height: 48,
        fontSize: '0.8rem',
        borderBottom: '2px solid #e0e0e0',
        position: 'relative',
        ...sx,
      }}
    >
      {tooltip ? (
        <Tooltip title={tooltip} arrow placement="top">
          {cellContent}
        </Tooltip>
      ) : (
        cellContent
      )}
    </TableCell>
  );
};

export default SortableTableCell;
